import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { FlexDirection, Align, PositionType } from '../renderer.js';
import { Container } from './Container.js';
import { Panel } from './Panel.js';
/**
 * A progress bar component that displays a value between 0 and 1 as a filled bar.
 * Supports customizable foreground and background styling.
 *
 * @component
 * @example
 * ```tsx
 * <ProgressBar
 *   value={0.75}
 *   bgColor="#e0e0e0"
 *   fgColor="#4caf50"
 *   height={20}
 *   width={200}
 * >
 *   <Text>75%</Text>
 * </ProgressBar>
 * ```
 *
 * @param {YogaNodeProps & Object} props - The progress bar properties
 * @param {number} props.value - Progress value between 0 and 1 (will be clamped if outside range)
 * @param {number} [props.rounding=30] - Corner rounding radius for the progress bar
 * @param {Material} [props.fgMaterial] - Material for the foreground (filled) bar
 * @param {Material} [props.bgMaterial] - Material for the background bar
 * @param {Color} [props.fgColor] - Color for the foreground (filled) bar
 * @param {Color} [props.bgColor] - Color for the background bar
 * @param {number} [props.barLeftMargin=12] - Left margin for the content overlay
 * @param {React.ReactNode} props.children - Content to display over the progress bar (e.g., percentage text)
 * @param {React.Ref<Object3D>} ref - Forward ref to access the underlying 3D object
 * @returns {React.ReactElement} A progress bar element
 */
export const ProgressBar = forwardRef((props, ref) => {
    var _a, _b, _c, _d, _e;
    const rounding = (_a = props.rounding) !== null && _a !== void 0 ? _a : 30;
    const value = Math.max(Math.min(1, props.value), 0); // clamp between 0 and 1
    return (_jsxs(Panel, Object.assign({ material: props.bgMaterial, backgroundColor: props.bgColor }, props, { flexDirection: FlexDirection.Row, padding: (_b = props.padding) !== null && _b !== void 0 ? _b : 6, paddingLeft: (_c = props.paddingLeft) !== null && _c !== void 0 ? _c : 8, paddingRight: (_d = props.paddingRight) !== null && _d !== void 0 ? _d : 8, resolution: 6, rounding: rounding * 1.5, ref: ref, children: [_jsx(Container, { alignItems: Align.FlexStart, position: PositionType.Absolute, width: "100%", height: "100%", left: (_e = props.barLeftMargin) !== null && _e !== void 0 ? _e : 12, children: props.children }), _jsx(Panel, { width: `${100 * value}%`, minWidth: rounding * 2, height: "100%", material: props.fgMaterial, backgroundColor: props.fgColor, alignItems: Align.Center, rounding: rounding })] })));
});
ProgressBar.displayName = 'ProgressBar';
